import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router';
import { api } from './api.ts';
import { authClient } from './auth-client.ts';
import { setLang } from './i18n/index.ts';
import { CalendarPage } from './pages/CalendarPage.tsx';
import { ConfirmEmailPage, ForgotPage, InvitePage, ResetPage } from './pages/AccountPages.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { MyBookingsPage } from './pages/MyBookingsPage.tsx';
import { ProfilePage } from './pages/ProfilePage.tsx';
import { RoomsPage } from './pages/RoomsPage.tsx';
import { TwoFactorSetupPage } from './pages/TwoFactorSetupPage.tsx';
import { useMe } from './queries.ts';

// Staff-only screens load separately so tenants download less.
const AdminPage = lazy(() =>
  import('./admin/AdminPage.tsx').then((m) => ({ default: m.AdminPage })),
);
const QrSignPage = lazy(() =>
  import('./admin/QrSignPage.tsx').then((m) => ({ default: m.QrSignPage })),
);
import { isStaff, type Me } from './types.ts';

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const online = useOnline();
  const brand = me.site.brand.primaryColor;

  useEffect(() => {
    if (brand) document.documentElement.style.setProperty('--navy', brand);
  }, [brand]);

  const toggleLang = async () => {
    const next = i18n.language === 'sv' ? 'en' : 'sv';
    setLang(next);
    await api.patch('/api/me', { language: next }).catch(() => {});
  };
  const signOut = async () => {
    await authClient.signOut();
    qc.clear();
    window.location.assign('/login');
  };

  return (
    <>
      <a className="skip" href="#main">
        {t('app.skip')}
      </a>
      <header className="top">
        <h1>
          {me.site.hasLogo && <img src="/api/site/logo" alt="" />}
          {me.site.name}
        </h1>
        <span className="who">
          {me.firstName} {me.lastName}
          {me.companyName ? ` · ${me.companyName}` : ''}
        </span>
        <button type="button" onClick={toggleLang} lang={i18n.language === 'sv' ? 'en' : 'sv'}>
          {t('nav.language')}
        </button>
        <button type="button" onClick={signOut}>
          {t('nav.signOut')}
        </button>
      </header>
      {!online && <div className="offline">{t('app.offline')}</div>}
      <nav className="tabs" aria-label={t('nav.main')}>
        <NavLink to="/" end>
          {t('nav.calendar')}
        </NavLink>
        <NavLink to="/rooms">{t('nav.rooms')}</NavLink>
        <NavLink to="/bookings">{t('nav.bookings')}</NavLink>
        {isStaff(me) && <NavLink to="/admin">{t('nav.admin')}</NavLink>}
        <NavLink to="/profile">{t('nav.profile')}</NavLink>
      </nav>
      <main id="main">{children}</main>
    </>
  );
}

export function App() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();

  useEffect(() => {
    if (me.data?.language) setLang(me.data.language);
  }, [me.data?.language]);

  const publicRoutes = (
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPage />} />
      <Route path="/reset-password" element={<ResetPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
    </>
  );

  if (me.isLoading) return <main className="narrow">{t('common.loading')}</main>;

  if (!me.data) {
    return (
      <Routes>
        {publicRoutes}
        <Route
          path="*"
          element={
            <Navigate
              to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
              replace
            />
          }
        />
      </Routes>
    );
  }

  if (isStaff(me.data) && !me.data.twoFactorEnabled) {
    return (
      <Routes>
        {publicRoutes}
        <Route path="*" element={<TwoFactorSetupPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {publicRoutes}
      <Route
        path="/admin/rooms/:id/sign"
        element={
          <Suspense fallback={null}>
            <QrSignPage />
          </Suspense>
        }
      />
      <Route path="*" element={<LoggedIn me={me.data} />} />
    </Routes>
  );
}

function LoggedIn({ me }: { me: Me }) {
  return (
    <Shell me={me}>
      <Routes>
        <Route path="/" element={<CalendarPage me={me} />} />
        <Route path="/room/:roomId" element={<CalendarPage me={me} />} />
        <Route path="/rooms" element={<RoomsPage me={me} />} />
        <Route path="/bookings" element={<MyBookingsPage me={me} />} />
        <Route path="/profile" element={<ProfilePage me={me} />} />
        {isStaff(me) && (
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={null}>
                <AdminPage me={me} />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
